import { FlowEventHandlers } from '../../../shared/flow-event';
import {
  SignupByOtpOneCredentialOptionalFlowEventDefinition,
  SignupByOtpOneCredentialOptionalFlowEventState,
} from './types';
import { appTimingUtil, monitor } from '../../../platform/monitoring';
import { BusinessPerfMetric } from '../../../platform/monitoring/performance/enums';
import { PageName } from '@shopee-user/url';
import {
  generateTrackingHelpButtonClick,
  generateTrackingNextButtonClick,
  generateTrackingSignupView,
  generateTrackingSubmitFormButtonClick,
  TrackingDataFieldTypeSignupMethod,
  generateTrackingSignupAlreadyRegisteredView,
  generateTrackingAlreadyRegisteredLoginButtonClick,
  generateTrackingAlreadyRegisteredHelpButtonClick,
  generateTrackingAlreadyRegisteredContactUsButtonClick,
  TrackingDataFieldTypeAccountType,
  generateTrackingSignupSetPasswordView,
  generateTrackingSignupSetPasswordSignupButtonClick,
  generateTrackingSignupSetPasswordBackButtonClick,
  generateTrackingSignupSuccessView,
  generateTrackingSignupSuccessBackButtonClick,
  generateTrackingSignupSuccessAction,
  generateTrackingSignupSuccessNextButtonClick,
} from '@shopee-user/tracking-signup';
import { sendGTMEventSignupSuccess } from '@tob/shared/tracking/signup';
import {
  generateTrackingAccountBannedContactUsButtonClick,
  generateTrackingAccountBannedOkButtonClick,
  generateTrackingAccountBannedImpression,
  TrackingDataFieldTypeOtpScenario,
} from '@shopee-user/tracking-otp-enter-verification';
import { generateTrackingLoginSuccessAction } from '@shopee-user/tracking-login';
import { AuthKeyType, OtpType } from '@tob/shared/otp/base-hook/constants';
import {
  sendLoginResult,
  sendSignupResult,
} from '@tob/shared/monitoring/business-metric';
import {
  AccType,
  BusinessPage,
  LoginMethod,
  SignupMethod,
  Status,
} from '@tob/shared/monitoring/enums';
import {
  toMonitoringLoginMethod,
  toMonitoringSignupMethod,
} from '@tob/shared/monitoring/business-metric/utils';
import {
  toTrackingAccountType,
  toTrackingLoginMethod,
  toTrackingSignupMethod,
} from '@tob/shared/tracking/utils';

const OTP_SCENARIO = TrackingDataFieldTypeOtpScenario.Signup;

export const TRACKER_NAME = 'signup-by-otp-one-credential-optional';

export const createInitialState = (): SignupByOtpOneCredentialOptionalFlowEventState => {
  return {
    authKey: '',
  };
};

export function createTracker() {
  const trackPageLoad = ({ pageName }: { pageName: string }) => {
    const softNavigationDuration = appTimingUtil.getDurationFromSoftNavigationStart();
    const isSoftNavigation = softNavigationDuration > 0;

    monitor.sendDuration({
      name: BusinessPerfMetric.PageLoad,
      duration: isSoftNavigation
        ? softNavigationDuration
        : appTimingUtil.getAppAliveDuration(),
      data: {
        soft_nav: isSoftNavigation ? 'true' : 'false',
        page_name: pageName,
        // some of custom metadatas are not allowed to be accessed in Grafana because of database performance concerns
        // so here we use status to indicate the navigation type
        status: isSoftNavigation ? 'soft_nav' : 'hard_nav',
      },
    });
  };

  const trackSignupResult = ({
    status,
    signupMethod,
    accType,
    error,
    apiSource,
  }: {
    status: Status;
    signupMethod: SignupMethod;
    accType: AccType;
    error?: number;
    apiSource?: string;
  }) => {
    sendSignupResult({
      status,
      signup_method: signupMethod,
      business_page: BusinessPage.SignupByOtpOneCredentialOptional,
      acc_type: accType,
      error,
      api_source: apiSource,
    });
  };

  const trackLoginResult = ({
    status,
    accType,
    loginMethod,
    error,
    apiSource,
  }: {
    status: Status;
    accType: AccType;
    loginMethod: LoginMethod;
    error?: number;
    apiSource?: string;
  }) => {
    sendLoginResult({
      status,
      login_method: loginMethod,
      business_page: BusinessPage.SignupByOtpOneCredentialOptional,
      acc_type: accType,
      error,
      api_source: apiSource,
    });
  };

  const handlers: FlowEventHandlers<
    SignupByOtpOneCredentialOptionalFlowEventDefinition,
    SignupByOtpOneCredentialOptionalFlowEventState
  > = {
    onSignupRequireAuthKeyPageInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSignupView());
        trackPageLoad({
          pageName: PageName.PageSignupByOTP,
        });
      };
    },
    onSignupSubmitAuthKey: ({ phone }: { phone: string | undefined }) => {
      return ({ tools }) => {
        const authKey = phone
          ? TrackingDataFieldTypeSignupMethod.Phone
          : TrackingDataFieldTypeSignupMethod.Email;
        tools.sendBITrackingEvent(
          generateTrackingNextButtonClick({
            method: authKey,
          })
        );

        return { authKey };
      };
    },
    onSignupSignupHelpClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingHelpButtonClick());
      };
    },
    onSignupSubmitExternalSignupFormClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSubmitFormButtonClick());
      };
    },
    onSignupAccountBannedPageInit: () => {
      return ({ tools }) => {
        tools.sendImpressionEvent(
          generateTrackingAccountBannedImpression({ scenario: OTP_SCENARIO })
        );
      };
    },
    onSignupAccountBannedOkClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedOkButtonClick({ scenario: OTP_SCENARIO })
        );
      };
    },
    onSignupAccountBannedContactUsClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedContactUsButtonClick({
            scenario: OTP_SCENARIO,
          })
        );
      };
    },
    onSignupAlreadyRegisteredPageInit: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupAlreadyRegisteredView({ method })
        );
      };
    },
    onSignupAlreadyRegisteredLoginClick: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAlreadyRegisteredLoginButtonClick({ method })
        );
      };
    },
    onSignupAlreadyRegisteredHelpClick: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAlreadyRegisteredHelpButtonClick({ method })
        );
      };
    },
    onSignupAlreadyRegisteredContactUsClick: ({
      method,
    }: {
      method: TrackingDataFieldTypeSignupMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAlreadyRegisteredContactUsButtonClick({ method })
        );
      };
    },
    onSignupSetPasswordPageInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSignupSetPasswordView());
      };
    },
    onSignupSubmitPassword: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSetPasswordSignupButtonClick()
        );
      };
    },
    onSignupSetPasswordBackClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSetPasswordBackButtonClick()
        );
      };
    },
    onSignupSignupSuccessInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(generateTrackingSignupSuccessView());
      };
    },
    onSignupSuccess: ({ userId }: { userId: number }) => {
      return ({ tools, state }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSuccessAction({
            accountType: TrackingDataFieldTypeAccountType.TobAccount,
            method: toTrackingSignupMethod(state.authKey),
            otpType:
              state.authKey === AuthKeyType.Email ? OtpType.Email : OtpType.Sms,
          })
        );

        sendGTMEventSignupSuccess({
          userId,
          method: toTrackingSignupMethod(state.authKey),
          accountType: TrackingDataFieldTypeAccountType.TobAccount,
        });

        trackSignupResult({
          status: Status.Success,
          accType: AccType.Tob,
          signupMethod: toMonitoringSignupMethod(state.authKey),
        });
      };
    },
    onSignupFailed: ({
      error,
      apiSource,
    }: {
      error: number;
      apiSource: string;
    }) => {
      return ({ state }) => {
        trackSignupResult({
          status: Status.Failed,
          accType: AccType.Tob,
          signupMethod: toMonitoringSignupMethod(state.authKey),
          error,
          apiSource,
        });
      };
    },
    onSignupSignupSuccessBackClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSuccessBackButtonClick()
        );
      };
    },
    onSignupSignupSuccessNextClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingSignupSuccessNextButtonClick()
        );
      };
    },
    onSignupLoginSuccess: ({ accType }: { accType: AccType }) => {
      return ({ tools, state }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSuccessAction({
            loginMethod: toTrackingLoginMethod(state.authKey),
            accountType: toTrackingAccountType(accType),
          })
        );

        trackLoginResult({
          status: Status.Success,
          accType,
          loginMethod: toMonitoringLoginMethod(state.authKey),
        });
      };
    },
    onSignupLoginFailed: ({
      accType,
      error,
      apiSource,
    }: {
      accType: AccType;
      error: number;
      apiSource: string;
    }) => {
      return ({ state }) => {
        trackLoginResult({
          status: Status.Failed,
          loginMethod: toMonitoringLoginMethod(state.authKey),
          accType,
          error,
          apiSource,
        });
      };
    },
  };

  return {
    name: TRACKER_NAME,
    handlers,
  };
}
